import React from 'react';
import { Link } from 'react-router-dom';
import { PageSEO } from '../../components/SEO/PageSEO';
import MarketingLayout from '../../components/marketing/MarketingLayout';
import { BriefcaseIcon, ChartBarIcon, ShieldCheckIcon, UsersIcon, CalculatorIcon, DocumentCheckIcon, ArrowTrendingUpIcon, CurrencyDollarIcon } from '@heroicons/react/24/outline';

const BusinessOwnerCommandCenter: React.FC = () => {
  const commonMistakes = [
    {
      mistake: "Personal ownership of corporate insurance",
      consequence: "Double taxation on death benefits",
      solution: "Proper corporate structures and beneficiary design"
    },
    {
      mistake: "Inadequate buy-sell funding",
      consequence: "Forced sale or family disputes",
      solution: "Fully funded agreements with annual reviews"
    },
    {
      mistake: "No key person protection",
      consequence: "Business fails if key employee lost",
      solution: "Strategic coverage on critical personnel"
    },
    {
      mistake: "Ignoring holding company opportunities",
      consequence: "Missed tax savings and creditor protection",
      solution: "Optimal corporate structure design"
    }
  ];

  const businessStages = [
    {
      stage: "Startup Phase",
      revenue: "Under $1M",
      priorities: ["Founder protection", "Initial key person", "Basic succession"],
      strategies: ["Term insurance foundation", "Disability for founders", "Buy-sell preparation"]
    },
    {
      stage: "Growth Phase",
      revenue: "$1M - $5M",
      priorities: ["Key employee retention", "Tax optimization", "Succession planning"],
      strategies: ["Corporate-owned insurance", "Deferred compensation", "Estate freeze consideration"]
    },
    {
      stage: "Established Business",
      revenue: "$5M - $25M",
      priorities: ["Wealth extraction", "Multi-generational planning", "Exit strategies"],
      strategies: ["Holding company structures", "Insurance retirement plans", "Family trust integration"]
    },
    {
      stage: "Enterprise Level",
      revenue: "$25M+",
      priorities: ["Complex succession", "Tax minimization", "Legacy planning"],
      strategies: ["Multiple holding companies", "Charitable strategies", "Cross-border planning"]
    }
  ];

  const industrySpecialties = [
    {
      industry: "Professional Corporations",
      icon: <BriefcaseIcon className="h-8 w-8" />,
      needs: ["Income protection", "Corporate tax strategies", "Partnership insurance"],
      solutions: ["Professional disability plans", "Capital dividend optimization", "Cross-purchase agreements"]
    },
    {
      industry: "Agriculture & Farming",
      icon: <ChartBarIcon className="h-8 w-8" />,
      needs: ["Succession planning", "Asset protection", "Income smoothing"],
      solutions: ["Multi-generational transfers", "Creditor protection strategies", "Corporate farming structures"]
    },
    {
      industry: "Oil & Energy",
      icon: <ArrowTrendingUpIcon className="h-8 w-8" />,
      needs: ["Cyclical income protection", "Key person coverage", "Exit planning"],
      solutions: ["Flexible premium strategies", "Executive benefits", "Leveraged buyouts"]
    },
    {
      industry: "Technology & Innovation",
      icon: <CurrencyDollarIcon className="h-8 w-8" />,
      needs: ["Founder protection", "Employee retention", "IP protection"],
      solutions: ["Vesting insurance programs", "Golden handcuff plans", "Buy-sell for rapid growth"]
    }
  ];

  return (
    <MarketingLayout>
      <PageSEO
        title="Business Owner Command Center - Corporate Insurance & Succession Planning | PCM"
        description="Sophisticated insurance strategies for business owners. Avoid double taxation, fund buy-sell agreements, protect key employees, and optimize corporate structures with PCM's private advisory."
        keywords={[
          'business insurance Saskatchewan',
          'corporate life insurance',
          'buy sell agreement funding',
          'key person insurance',
          'business succession planning',
          'corporate tax strategies',
          'holding company insurance',
          'executive benefits'
        ]}
      />

      <div className="min-h-screen bg-white">
        {/* Hero Section */}
        <section className="relative bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 text-white py-24">
          <div className="absolute inset-0 bg-black opacity-30"></div>
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-5xl mx-auto text-center">
              <p className="text-amber-400 text-sm tracking-widest uppercase mb-4">Business Owner Command Center</p>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-light mb-6 leading-tight">
                Corporate Insurance Strategies<br />
                That Protect Your Empire
              </h1>
              <p className="text-xl text-gray-300 font-light mb-10 max-w-3xl mx-auto">
                Your business is your life's work. Protect it with sophisticated strategies that 
                minimize taxes, ensure continuity, and preserve your legacy. No cookie-cutter solutions.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link 
                  to="/contact" 
                  className="inline-block bg-amber-500 hover:bg-amber-600 text-white font-medium py-4 px-10 rounded transition duration-300 text-center tracking-wide"
                >
                  Schedule Executive Consultation
                </Link>
                <a 
                  href="#common-mistakes"
                  className="inline-block bg-transparent border-2 border-white hover:bg-white hover:text-slate-900 text-white font-medium py-4 px-10 rounded transition duration-300 text-center tracking-wide"
                >
                  Avoid Costly Mistakes
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Value Indicators */}
        <section className="py-8 bg-gray-50 border-b border-gray-200">
          <div className="container mx-auto px-4">
            <div className="flex flex-wrap items-center justify-center gap-8 text-sm text-gray-600">
              <div className="flex items-center gap-2">
                <DocumentCheckIcon className="h-5 w-5 text-amber-600" />
                <span>$500M+ Protected</span>
              </div>
              <div className="flex items-center gap-2">
                <CalculatorIcon className="h-5 w-5 text-amber-600" />
                <span>$50M+ Tax Savings</span>
              </div>
              <div className="flex items-center gap-2">
                <UsersIcon className="h-5 w-5 text-amber-600" />
                <span>200+ Businesses Secured</span>
              </div>
              <div className="flex items-center gap-2">
                <ShieldCheckIcon className="h-5 w-5 text-amber-600" />
                <span>Zero Succession Failures</span>
              </div>
            </div>
          </div>
        </section>

        {/* Common Mistakes Section */}
        <section id="common-mistakes" className="py-20">
          <div className="container mx-auto px-4">
            <div className="max-w-5xl mx-auto">
              <div className="text-center mb-16">
                <h2 className="text-3xl md:text-4xl font-light mb-4">
                  Costly Mistakes We Prevent
                </h2>
                <p className="text-xl text-gray-600">
                  Most business owners make these errors. Our private advisory ensures you don't.
                </p>
              </div>

              <div className="space-y-8">
                {commonMistakes.map((item, index) => (
                  <div key={index} className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
                    <div className="grid lg:grid-cols-3 gap-6 items-center">
                      <div>
                        <h3 className="text-lg font-semibold text-red-700 mb-2">The Mistake:</h3>
                        <p className="text-gray-700">{item.mistake}</p>
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-orange-700 mb-2">The Consequence:</h3>
                        <p className="text-gray-700">{item.consequence}</p>
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-green-700 mb-2">Our Solution:</h3>
                        <p className="text-gray-700">{item.solution}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-12 bg-amber-50 border-2 border-amber-200 rounded-lg p-8 text-center">
                <p className="text-lg text-gray-700 italic mb-4">
                  "PCM saved us $2.3 million in taxes by restructuring our corporate insurance 
                  before our exit. Their coordination with our accountant and lawyer was flawless."
                </p>
                <p className="text-amber-700 font-semibold">- Tech Founder, $40M Exit</p>
              </div>
            </div>
          </div>
        </section>

        {/* Business Lifecycle Solutions */}
        <section className="py-20 bg-slate-50">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-16">
                <h2 className="text-3xl md:text-4xl font-light mb-4">
                  Strategies for Every Business Stage
                </h2>
                <p className="text-xl text-gray-600">
                  Your needs evolve. So do our solutions.
                </p>
              </div>

              <div className="space-y-8">
                {businessStages.map((stage, index) => (
                  <div key={index} className="bg-white rounded-lg shadow-sm p-8">
                    <div className="grid lg:grid-cols-4 gap-6">
                      <div>
                        <h3 className="text-xl font-semibold mb-2">{stage.stage}</h3>
                        <p className="text-amber-600 font-medium">{stage.revenue}</p>
                      </div>
                      <div>
                        <h4 className="text-sm text-gray-500 mb-2">Key Priorities:</h4>
                        <ul className="space-y-1">
                          {stage.priorities.map((priority, idx) => (
                            <li key={idx} className="text-gray-700 text-sm">• {priority}</li>
                          ))}
                        </ul>
                      </div>
                      <div className="lg:col-span-2">
                        <h4 className="text-sm text-gray-500 mb-2">Recommended Strategies:</h4>
                        <ul className="space-y-1">
                          {stage.strategies.map((strategy, idx) => (
                            <li key={idx} className="text-gray-700 text-sm">✓ {strategy}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Industry Specialization */}
        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-16">
                <h2 className="text-3xl md:text-4xl font-light mb-4">
                  Industry-Specific Expertise
                </h2>
                <p className="text-xl text-gray-600">
                  Deep understanding of your sector's unique challenges
                </p>
              </div>

              <div className="grid md:grid-cols-2 gap-8">
                {industrySpecialties.map((industry, index) => (
                  <div key={index} className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
                    <div className="flex items-start gap-4 mb-6">
                      <div className="text-amber-600">{industry.icon}</div>
                      <h3 className="text-xl font-semibold">{industry.industry}</h3>
                    </div>
                    
                    <div className="grid lg:grid-cols-2 gap-6">
                      <div>
                        <h4 className="text-sm text-gray-500 mb-3">Common Needs:</h4>
                        <ul className="space-y-2">
                          {industry.needs.map((need, idx) => (
                            <li key={idx} className="text-gray-700 text-sm flex items-start">
                              <span className="text-amber-500 mr-2">•</span>
                              {need}
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div>
                        <h4 className="text-sm text-gray-500 mb-3">Our Solutions:</h4>
                        <ul className="space-y-2">
                          {industry.solutions.map((solution, idx) => (
                            <li key={idx} className="text-gray-700 text-sm flex items-start">
                              <span className="text-green-500 mr-2">✓</span>
                              {solution}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Core Services */}
        <section className="py-20 bg-gray-50">
          <div className="container mx-auto px-4">
            <div className="max-w-5xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-light mb-16 text-center">
                Comprehensive Business Protection Services
              </h2>

              <div className="grid lg:grid-cols-3 gap-8">
                {/* Buy-Sell Agreements */}
                <div className="bg-white rounded-lg p-8 shadow-sm">
                  <ShieldCheckIcon className="h-12 w-12 text-amber-600 mb-6" />
                  <h3 className="text-xl font-semibold mb-4">Buy-Sell Agreements</h3>
                  <ul className="text-gray-600 space-y-2 mb-6">
                    <li>• Valuation methodology design</li>
                    <li>• Funding structure optimization</li>
                    <li>• Tax-efficient transfers</li>
                    <li>• Trigger event planning</li>
                    <li>• Annual review protocols</li>
                  </ul>
                  <Link to="/services/buy-sell-agreement-funding" className="text-amber-600 font-medium hover:text-amber-700">
                    Learn More →
                  </Link>
                </div>

                {/* Key Person Insurance */}
                <div className="bg-white rounded-lg p-8 shadow-sm">
                  <UsersIcon className="h-12 w-12 text-amber-600 mb-6" />
                  <h3 className="text-xl font-semibold mb-4">Key Person Protection</h3>
                  <ul className="text-gray-600 space-y-2 mb-6">
                    <li>• Impact analysis</li>
                    <li>• Coverage calculation</li>
                    <li>• Recruitment protection</li>
                    <li>• Loan coverage</li>
                    <li>• Retention strategies</li>
                  </ul>
                  <Link to="/services/key-person-insurance" className="text-amber-600 font-medium hover:text-amber-700">
                    Learn More →
                  </Link>
                </div>

                {/* Corporate Structures */}
                <div className="bg-white rounded-lg p-8 shadow-sm">
                  <BriefcaseIcon className="h-12 w-12 text-amber-600 mb-6" />
                  <h3 className="text-xl font-semibold mb-4">Corporate Structures</h3>
                  <ul className="text-gray-600 space-y-2 mb-6">
                    <li>• Holding company design</li>
                    <li>• Creditor protection</li>
                    <li>• Tax optimization</li>
                    <li>• Estate freeze strategies</li>
                    <li>• Family trust integration</li>
                  </ul>
                  <Link to="/services/insured-retirement-strategy-irs" className="text-amber-600 font-medium hover:text-amber-700">
                    Learn More →
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Private Advisory Process */}
        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-light mb-16 text-center">
                Your Business Advisory Journey
              </h2>

              <div className="space-y-12">
                <div className="flex items-start gap-6">
                  <div className="bg-amber-500 text-white rounded-full w-12 h-12 flex items-center justify-center flex-shrink-0 font-semibold">
                    1
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold mb-3">Strategic Discovery</h3>
                    <p className="text-gray-600 mb-3">
                      Comprehensive analysis of your business structure, ownership, key personnel, 
                      and long-term objectives. We review existing agreements and insurance.
                    </p>
                    <div className="bg-gray-50 rounded p-4">
                      <p className="text-sm text-gray-700">
                        <strong>Key Focus:</strong> Tax structure, succession plans, risk exposures, growth trajectory
                      </p>
                    </div>
                  </div>
                </div>

                <div className="flex items-start gap-6">
                  <div className="bg-amber-500 text-white rounded-full w-12 h-12 flex items-center justify-center flex-shrink-0 font-semibold">
                    2
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold mb-3">Professional Coordination</h3>
                    <p className="text-gray-600 mb-3">
                      We collaborate with your accountant and lawyer to ensure perfect alignment 
                      of insurance strategies with tax planning and legal structures.
                    </p>
                    <div className="bg-gray-50 rounded p-4">
                      <p className="text-sm text-gray-700">
                        <strong>Integration:</strong> Tax optimization, legal compliance, financial efficiency
                      </p>
                    </div>
                  </div>
                </div>

                <div className="flex items-start gap-6">
                  <div className="bg-amber-500 text-white rounded-full w-12 h-12 flex items-center justify-center flex-shrink-0 font-semibold">
                    3
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold mb-3">Custom Strategy Design</h3>
                    <p className="text-gray-600 mb-3">
                      Bespoke solutions addressing your specific risks, opportunities, and objectives. 
                      Every recommendation is tailored to your industry and growth stage.
                    </p>
                    <div className="bg-gray-50 rounded p-4">
                      <p className="text-sm text-gray-700">
                        <strong>Deliverables:</strong> Written strategy, financial modeling, implementation roadmap
                      </p>
                    </div>
                  </div>
                </div>

                <div className="flex items-start gap-6">
                  <div className="bg-amber-500 text-white rounded-full w-12 h-12 flex items-center justify-center flex-shrink-0 font-semibold">
                    4
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold mb-3">Ongoing Partnership</h3>
                    <p className="text-gray-600 mb-3">
                      Annual strategic reviews, immediate support for business changes, and 
                      proactive adjustments as tax laws and your business evolve.
                    </p>
                    <div className="bg-gray-50 rounded p-4">
                      <p className="text-sm text-gray-700">
                        <strong>Support:</strong> Direct advisor access, annual reviews, change management
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ROI Calculator CTA */}
        <section className="py-16 bg-amber-500 text-white">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <CalculatorIcon className="h-16 w-16 mx-auto mb-6" />
              <h3 className="text-3xl font-light mb-4">
                Calculate Your Tax Savings Potential
              </h3>
              <p className="text-xl mb-8 text-amber-100">
                See how proper corporate insurance structures could save your business thousands in taxes
              </p>
              <Link 
                to="/calculators/corporate-tax-savings" 
                className="inline-block bg-white text-amber-600 hover:bg-gray-100 font-medium py-4 px-10 rounded transition duration-300"
              >
                Calculate Savings
              </Link>
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="py-20 bg-slate-900 text-white">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-3xl md:text-4xl font-light mb-6">
                Your Business Deserves<br />
                Institutional-Level Protection
              </h2>
              <p className="text-xl mb-10 text-gray-300 font-light">
                Join 200+ business owners who've secured their legacy with 
                PCM's private advisory.
              </p>
              <div className="flex flex-col sm:flex-row gap-6 justify-center">
                <Link 
                  to="/contact" 
                  className="inline-block bg-amber-500 hover:bg-amber-600 text-white font-medium py-4 px-12 rounded transition duration-300 tracking-wide"
                >
                  Schedule Executive Consultation
                </Link>
                <a 
                  href="tel:1-306-123-4567"
                  className="inline-block bg-transparent border-2 border-white hover:bg-white hover:text-slate-900 text-white font-medium py-4 px-12 rounded transition duration-300 tracking-wide"
                >
                  Direct: 1-306-123-4567
                </a>
              </div>
              <p className="mt-8 text-gray-400 text-sm">
                Confidential consultations for businesses $1M+ revenue
              </p>
            </div>
          </div>
        </section>
      </div>
    </MarketingLayout>
  );
};

export default BusinessOwnerCommandCenter;